import type {
  GameAction,
  LaneId,
  PlayerObservationV1,
  VisibleCardViewV1,
} from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

import { Battlefield3D } from "../battlefield-3d/Battlefield3D";
import type { HandScreenLayout } from "../battlefield-3d/hand-screen-layout";
import { useReducedMotion } from "../hooks/useReducedMotion";
import type { LaneViewId } from "../presentation/game-view-model";
import { type AlphaAttackPreview } from "./battlefield/AttackPathOverlay";
import type { AlphaCombatPresentationState } from "./battlefield/CombatPresentation";
import { DiscardZones } from "./battlefield/DiscardZones";
import { PortraitBattlefield } from "./battlefield/PortraitBattlefield";
import { useBattlefieldCamera } from "./camera/useBattlefieldCamera";
import { AlphaCardInspector } from "./cards/AlphaCardInspector";
import { getAlphaAutoProgression } from "./alpha-turn-progression";
import { useAlphaGameStore } from "./alpha-game-store";
import { hitTestAlphaLane } from "./drag/alpha-drag-hit-test";
import {
  alphaPlacementActionKey,
  alphaPlacementCardInstanceId,
  alphaPlacementLabel,
  isAlphaPlacementAction,
  type AlphaPlacementAction,
} from "./drag/alpha-drag-types";
import { useAlphaCardDrag } from "./drag/useAlphaCardDrag";
import { useAlphaFieldDisbandDrag, type AlphaDisbandAction } from "./drag/useAlphaFieldDisbandDrag";
import { useAlphaFormationDrag, type AlphaAttackAction } from "./drag/useAlphaFormationDrag";
import { AlphaHand } from "./hand/AlphaHand";
import { AlphaGameplayMenu } from "./hud/AlphaGameplayMenu";
import { createMatchHudLayout } from "./hud/hud-layout";
import { createMatchHudModel } from "./hud/match-hud-model";
import { NoTerritoryPresentation } from "./hud/NoTerritoryPresentation";
import { SemanticHudOverlay } from "./hud/SemanticHudOverlay";
import { getAlphaDisplayName } from "./presentation/alpha-card-presentation";
import { ALPHA_DECK_PRESENTATION } from "./presentation/alpha-deck-presentation";
import { ActionPresentationDirector } from "./presentation/ActionPresentationDirector";
import { alphaPresentationDuration } from "./presentation/presentation-transaction";
import {
  alphaExportFilename,
  publicActionSummary,
  validateAlphaSeed,
  type AlphaDeckId,
  type AlphaMatchConfig,
} from "./alpha-session-controller";
import {
  AlphaActionConfirmation,
  AlphaCommandChoicePanel,
  AlphaSupplyChoicePanel,
  type AlphaPendingAction,
} from "./AlphaActionPanels";
import { AlphaGameIcon } from "./icons/AlphaGameIcon";
import { AlphaQuickGuide } from "./tutorial/AlphaQuickGuide";
import { AlphaTutorialOverlay } from "./tutorial/AlphaTutorialOverlay";
import {
  getAlphaObjectiveRecommendation,
  getTutorialConceptsForAcceptedAction,
  getTutorialHighlight,
} from "./tutorial/alpha-tutorial-guide";
import { useAlphaTutorialStore } from "./tutorial/alpha-tutorial-store";
import type { AlphaMatchMode } from "./tutorial/alpha-tutorial-types";
import "./art-direction/alpha-theme.css";
import "../styles/alpha.css";
import "../styles/alpha-cards.css";
import "../styles/alpha-tutorial.css";
import "../styles/alpha-premium.css";

interface AlphaAppProps {
  onOpenDeveloperMode: () => void;
}

function readAlphaViewportSize(surface: HTMLElement | null): {
  readonly width: number;
  readonly height: number;
} {
  const surfaceBounds = surface?.getBoundingClientRect();
  return {
    width: Math.max(
      1,
      Math.round(
        surfaceBounds !== undefined && surfaceBounds.width > 0
          ? surfaceBounds.width
          : document.documentElement.clientWidth || window.innerWidth,
      ),
    ),
    height: Math.max(
      1,
      Math.round(
        surfaceBounds !== undefined && surfaceBounds.height > 0
          ? surfaceBounds.height
          : document.documentElement.clientHeight || window.innerHeight,
      ),
    ),
  };
}

function useAlphaViewportSize(surfaceRef: RefObject<HTMLElement | null>): {
  readonly width: number;
  readonly height: number;
} {
  const [viewport, setViewport] = useState(() => readAlphaViewportSize(null));
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    let animationFrame = 0;
    const update = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        setViewport(readAlphaViewportSize(surface));
      });
    };
    const resizeObserver =
      surface === null || typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    if (surface !== null) resizeObserver?.observe(surface);
    window.addEventListener("resize", update);
    update();
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [surfaceRef]);
  return viewport;
}

function cardActionId(action: GameAction): string | null {
  if (isAlphaPlacementAction(action)) return alphaPlacementCardInstanceId(action);
  return action.type === "play-event" ? action.cardInstanceId : null;
}

function disbandCardInstanceId(action: AlphaDisbandAction): string {
  return action.type === "disband-army"
    ? action.armyCardInstanceId
    : action.commanderCardInstanceId;
}

function disbandActionKey(action: AlphaDisbandAction): string {
  return `${action.type}:${action.playerId}:${action.laneId}:${disbandCardInstanceId(action)}`;
}

function placementActionTitle(action: AlphaPlacementAction, cardName: string): string {
  switch (action.type) {
    case "found-land":
      return `Found ${cardName}`;
    case "develop-land":
      return `Develop ${cardName}`;
    case "deploy-commander":
      return `Deploy ${cardName}`;
    case "replace-commander":
      return `Replace Commander with ${cardName}`;
    case "deploy-army":
      return `Station ${cardName}`;
    default:
      return `Play ${cardName}`;
  }
}

function attackActionKey(action: AlphaAttackAction): string {
  return `${action.playerId}:${action.laneId}:${action.targetLaneId ?? action.laneId}`;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function publicAttackPreview(
  observation: PlayerObservationV1,
  action: AlphaAttackAction,
): AlphaAttackPreview {
  const targetLaneId = action.targetLaneId ?? action.laneId;
  const attack = observation.lanes[action.laneId].own.formation?.totalAttack ?? 0;
  const guard = observation.lanes[targetLaneId].opponent.formation?.totalGuard ?? 0;
  return {
    sourceLaneId: action.laneId,
    targetLaneId,
    kind: targetLaneId === action.laneId ? "Direct" : "Flank",
    attack,
    guard,
    damage: Math.max(0, attack - guard),
  };
}

function publicLaneSignature(observation: PlayerObservationV1, laneId: LaneId): string {
  const lane = observation.lanes[laneId];
  return JSON.stringify({
    result: lane.result,
    own: [
      lane.own.status,
      lane.own.land?.stage,
      lane.own.land?.currentControl,
      lane.own.commander?.cardDefinitionId,
      lane.own.armies.map((army) => army.cardDefinitionId),
    ],
    opponent: [
      lane.opponent.status,
      lane.opponent.land?.stage,
      lane.opponent.land?.currentControl,
      lane.opponent.commander?.cardDefinitionId,
      lane.opponent.armies.map((army) => army.cardDefinitionId),
    ],
  });
}

function downloadJson(filename: string, json: string): boolean {
  let url: string | null = null;
  try {
    url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    const revokeObjectUrl = URL.revokeObjectURL.bind(URL);
    window.setTimeout(() => revokeObjectUrl(url!), 1_000);
    return true;
  } catch {
    if (url !== null && typeof URL.revokeObjectURL === "function") {
      const revokeObjectUrl = URL.revokeObjectURL.bind(URL);
      window.setTimeout(() => revokeObjectUrl(url!), 1_000);
    }
    return false;
  }
}

function SetupScreen({ onOpenDeveloperMode }: AlphaAppProps) {
  const start = useAlphaGameStore((state) => state.start);
  const storeValidationMessage = useAlphaGameStore((state) => state.validationMessage);
  const resetTutorial = useAlphaTutorialStore((state) => state.resetTutorial);
  const [config, setConfig] = useState<AlphaMatchConfig>({
    humanPlayerId: "player-1",
    humanDeckId: "assault",
    aiDeckId: "balanced",
    seed: "fate-kingdom-alpha-001",
  });
  const [matchMode, setMatchMode] = useState<AlphaMatchMode>("guided");
  const [seedError, setSeedError] = useState<string | null>(null);
  const seedRef = useRef<HTMLInputElement>(null);

  const update = <Key extends keyof AlphaMatchConfig>(key: Key, value: AlphaMatchConfig[Key]) => {
    setConfig((current) => ({ ...current, [key]: value }));
    if (key === "seed") setSeedError(null);
  };

  const startMatch = () => {
    const validated = validateAlphaSeed(config.seed);
    if (!validated.ok) {
      setSeedError(validated.message);
      seedRef.current?.focus();
      return;
    }
    const normalizedConfig = { ...config, seed: validated.seed };
    setConfig(normalizedConfig);
    setSeedError(null);
    resetTutorial(matchMode);
    start(normalizedConfig);
  };

  const setupError = seedError ?? storeValidationMessage;

  return (
    <main className="alpha-setup" data-testid="alpha-setup">
      <section className="alpha-setup__panel" aria-labelledby="alpha-title">
        <p className="alpha-kicker">Public Beta Preview</p>
        <h1 id="alpha-title">Fate Kingdom</h1>
        <p className="alpha-setup__lede">Develop the Land. Command the Army. Claim the Kingdom.</p>
        <section className="alpha-setup__goal" aria-label="Match goal">
          <AlphaGameIcon name="banner" aria-hidden="true" />
          <div>
            <strong>Claim two of three lanes to win.</strong>
            <span>Reduce enemy Land Control → earn Victory Banners.</span>
          </div>
        </section>
        <p className="alpha-setup__notice">
          The guide explains the action window as you play. You can skip or reopen it at any time.
        </p>

        <fieldset className="alpha-setup__mode">
          <legend>Match style</legend>
          <label>
            <input
              checked={matchMode === "guided"}
              name="alpha-match-mode"
              type="radio"
              onChange={() => setMatchMode("guided")}
            />
            <span>
              <strong>Guided First Match</strong>
              <small>Learn the objective and next useful step as you play.</small>
            </span>
          </label>
          <label>
            <input
              checked={matchMode === "standard"}
              name="alpha-match-mode"
              type="radio"
              onChange={() => setMatchMode("standard")}
            />
            <span>
              <strong>Standard Match</strong>
              <small>Play without the welcome guide. Help remains available.</small>
            </span>
          </label>
        </fieldset>

        <fieldset>
          <legend>Your seat</legend>
          <label>
            <input
              checked={config.humanPlayerId === "player-1"}
              name="alpha-seat"
              type="radio"
              onChange={() => update("humanPlayerId", "player-1")}
            />
            Player 1
          </label>
          <label>
            <input
              checked={config.humanPlayerId === "player-2"}
              name="alpha-seat"
              type="radio"
              onChange={() => update("humanPlayerId", "player-2")}
            />
            Player 2
          </label>
        </fieldset>

        <label className="alpha-field">
          Your deck
          <select
            value={config.humanDeckId}
            onChange={(event) => update("humanDeckId", event.target.value as AlphaDeckId)}
          >
            <option value="assault">Core Battle Assault</option>
            <option value="balanced">Core Battle Balanced</option>
          </select>
        </label>
        <label className="alpha-field">
          AI deck
          <select
            value={config.aiDeckId}
            onChange={(event) => update("aiDeckId", event.target.value as AlphaDeckId)}
          >
            <option value="balanced">Core Battle Balanced</option>
            <option value="assault">Core Battle Assault</option>
          </select>
        </label>
        <section className="alpha-deck-preview-grid" aria-label="Test-only Alpha deck previews">
          {(["assault", "balanced"] as const).map((deckId) => {
            const deck = ALPHA_DECK_PRESENTATION[deckId];
            const selected = config.humanDeckId === deckId;
            return (
              <button
                className={`alpha-deck-preview alpha-deck-preview--${deck.faction} ${selected ? "is-selected" : ""}`}
                key={deckId}
                type="button"
                aria-pressed={selected}
                onClick={() => update("humanDeckId", deckId)}
              >
                <img src={deck.artUrl} alt="" />
                <span>
                  <strong>{deck.displayName}</strong>
                  <small>
                    {deck.commanderName} · {deck.commanderStats.attack} Attack ·{" "}
                    {deck.commanderStats.guard} Guard · enables normal formation attacks
                  </small>
                  <small>{deck.composition}</small>
                </span>
              </button>
            );
          })}
        </section>
        <details className="alpha-setup__details">
          <summary>Advanced match options</summary>
          <label className="alpha-field">
            Match seed
            <input
              ref={seedRef}
              value={config.seed}
              spellCheck={false}
              aria-label="Match seed"
              aria-invalid={setupError !== null}
              aria-describedby="alpha-seed-help alpha-seed-error"
              onChange={(event) => update("seed", event.target.value)}
            />
            <small id="alpha-seed-help">
              A sensible default is ready. Seeds are trimmed before play; maximum 96 characters.
            </small>
          </label>
          <button
            className="alpha-link-button alpha-setup__developer"
            type="button"
            onClick={onOpenDeveloperMode}
          >
            Open Developer Mode
          </button>
        </details>
        <p id="alpha-seed-error" className="alpha-field-error" role="alert">
          {setupError}
        </p>

        <div className="alpha-setup__actions">
          <button className="alpha-button alpha-button--primary" type="button" onClick={startMatch}>
            Start match
          </button>
        </div>
      </section>
    </main>
  );
}

function AlphaBattleScreen({ onOpenDeveloperMode }: AlphaAppProps) {
  const controller = useAlphaGameStore((state) => state.controller);
  const snapshot = useAlphaGameStore((state) => state.snapshot);
  const sessionToken = useAlphaGameStore((state) => state.sessionToken);
  const paused = useAlphaGameStore((state) => state.paused);
  const quickAi = useAlphaGameStore((state) => state.quickAi);
  const contextStatus = useAlphaGameStore((state) => state.contextStatus);
  const validationMessage = useAlphaGameStore((state) => state.validationMessage);
  const presentationQueue = useAlphaGameStore((state) => state.presentationQueue);
  const applyHumanAction = useAlphaGameStore((state) => state.applyHumanAction);
  const stepAi = useAlphaGameStore((state) => state.stepAi);
  const acknowledgePresentation = useAlphaGameStore((state) => state.acknowledgePresentation);
  const setPaused = useAlphaGameStore((state) => state.setPaused);
  const setQuickAi = useAlphaGameStore((state) => state.setQuickAi);
  const setContextStatus = useAlphaGameStore((state) => state.setContextStatus);
  const start = useAlphaGameStore((state) => state.start);
  const leave = useAlphaGameStore((state) => state.leave);
  const tutorialMode = useAlphaTutorialStore((state) => state.mode);
  const tutorialEnabled = useAlphaTutorialStore((state) => state.enabled);
  const completedTutorialConcepts = useAlphaTutorialStore((state) => state.completedConcepts);
  const dismissedTutorialHint = useAlphaTutorialStore((state) => state.dismissedCurrentHint);
  const welcomeStep = useAlphaTutorialStore((state) => state.welcomeStep);
  const guideOpen = useAlphaTutorialStore((state) => state.guideOpen);
  const nextWelcome = useAlphaTutorialStore((state) => state.nextWelcome);
  const previousWelcome = useAlphaTutorialStore((state) => state.previousWelcome);
  const skipWelcome = useAlphaTutorialStore((state) => state.skipWelcome);
  const restartWelcome = useAlphaTutorialStore((state) => state.restartWelcome);
  const setGuideOpen = useAlphaTutorialStore((state) => state.setGuideOpen);
  const completeTutorialConcepts = useAlphaTutorialStore((state) => state.completeConcepts);
  const resetTutorial = useAlphaTutorialStore((state) => state.resetTutorial);
  const reducedMotion = useReducedMotion();
  const battlefieldSurfaceRef = useRef<HTMLDivElement>(null);
  const viewport = useAlphaViewportSize(battlefieldSurfaceRef);
  const [selectedCardId, setSelectedCardId] = useState<string | undefined>();
  const [focusedHandCardId, setFocusedHandCardId] = useState<string | undefined>();
  const [handScreenLayout, setHandScreenLayout] = useState<HandScreenLayout>({});
  const [selectedAttackSourceLaneId, setSelectedAttackSourceLaneId] = useState<LaneViewId>();
  const [inspectedCards, setInspectedCards] = useState<readonly VisibleCardViewV1[] | undefined>();
  const [pending, setPending] = useState<AlphaPendingAction | undefined>();
  const [menuOpen, setMenuOpen] = useState(false);
  const [combat, setCombat] = useState<AlphaCombatPresentationState | null>(null);
  const [noTerritoryAnnouncement, setNoTerritoryAnnouncement] = useState(false);
  const [aiFocusLaneId, setAiFocusLaneId] = useState<LaneId | undefined>();
  const [interactionMessage, setInteractionMessage] = useState<string | null>(null);
  const [placementPulseLaneId, setPlacementPulseLaneId] = useState<LaneViewId | undefined>();
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportFallback, setExportFallback] = useState<{
    readonly label: string;
    readonly json: string;
  } | null>(null);
  const [openingDrawVisible, setOpeningDrawVisible] = useState(snapshot?.actionCount === 0);
  const pendingTriggerRef = useRef<HTMLElement | null>(null);
  const pendingFromMenuRef = useRef(false);
  const inspectorTriggerRef = useRef<HTMLElement | null>(null);
  const menuTriggerRef = useRef<HTMLElement | null>(null);
  const resultDialogRef = useRef<HTMLElement>(null);
  const shellRef = useRef<HTMLElement>(null);
  const friendlyDiscardRef = useRef<HTMLDetailsElement>(null);
  const laneRefs = useRef(new Map<LaneViewId, HTMLButtonElement>());
  const selectionEpochRef = useRef({ sessionToken, paused, contextStatus });
  const progressionKeyRef = useRef<string | null>(null);
  const combatTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);
  const warningKeyRef = useRef<string | null>(null);
  const previousPublicObservationRef = useRef<PlayerObservationV1 | null>(null);
  const aiFocusTimerRef = useRef<number | null>(null);
  const openingControllerRef = useRef(controller);
  const selectionHistorySequenceRef = useRef(0);

  const observation = snapshot?.observation;
  const activePresentation = presentationQueue[0];
  const isHumanTurn = observation?.activePlayerId === observation?.perspectivePlayerId;
  const isAiTurn =
    observation !== undefined && !isHumanTurn && observation.status.type === "active";
  const selectedCard = observation?.own.hand.find((card) => card.cardInstanceId === selectedCardId);
  const selectionActive = selectedCardId !== undefined;
  const selectedCardActions = useMemo(
    () =>
      selectedCardId === undefined
        ? []
        : (snapshot?.humanLegalActions ?? []).filter(
            (action) => cardActionId(action) === selectedCardId,
          ),
    [selectedCardId, snapshot?.humanLegalActions],
  );
  const handLegalCardIds = useMemo(
    () =>
      new Set(
        (snapshot?.humanLegalActions ?? [])
          .map(cardActionId)
          .filter((cardInstanceId): cardInstanceId is string => cardInstanceId !== null),
      ),
    [snapshot?.humanLegalActions],
  );
  const selectedLegalTargetIds = selectedCardActions
    .map((action) => ("laneId" in action ? action.laneId : undefined))
    .filter((laneId): laneId is LaneViewId => laneId !== undefined);
  const attackActionsBySource = useMemo(() => {
    const actionsBySource = new Map<LaneViewId, Map<LaneViewId, AlphaAttackAction>>();
    for (const action of snapshot?.humanLegalActions ?? []) {
      if (action.type !== "attack") continue;
      const actionsByTarget = actionsBySource.get(action.laneId) ?? new Map();
      actionsByTarget.set(action.targetLaneId ?? action.laneId, action);
      actionsBySource.set(action.laneId, actionsByTarget);
    }
    return actionsBySource;
  }, [snapshot?.humanLegalActions]);
  const disbandActionsByLane = useMemo(() => {
    const actionsByLane = new Map<LaneViewId, Map<string, AlphaDisbandAction>>();
    for (const action of snapshot?.humanLegalActions ?? []) {
      if (action.type !== "disband-army" && action.type !== "disband-commander") continue;
      const actionsByCardId = actionsByLane.get(action.laneId) ?? new Map();
      actionsByCardId.set(disbandCardInstanceId(action), action);
      actionsByLane.set(action.laneId, actionsByCardId);
    }
    return actionsByLane;
  }, [snapshot?.humanLegalActions]);
  const selectedAttackTargetIds =
    selectedAttackSourceLaneId === undefined
      ? []
      : [...(attackActionsBySource.get(selectedAttackSourceLaneId)?.keys() ?? [])];
  const directActions = (snapshot?.humanLegalActions ?? []).filter(
    (action) => cardActionId(action) === null && action.type !== "attack",
  );
  const getLaneAtPoint = useCallback(
    (clientX: number, clientY: number) => hitTestAlphaLane(laneRefs.current, clientX, clientY),
    [],
  );
  const isFriendlyDiscardAtPoint = useCallback((clientX: number, clientY: number) => {
    const rect = friendlyDiscardRef.current?.getBoundingClientRect();
    return (
      rect !== undefined &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }, []);
  const clearSelection = useCallback((restoreFocus = false) => {
    setSelectedCardId(undefined);
    setSelectedAttackSourceLaneId(undefined);
    setInspectedCards(undefined);
    setPending(undefined);
    if (restoreFocus) {
      const trigger = pendingTriggerRef.current;
      window.setTimeout(() => trigger?.focus(), 0);
    }
    pendingTriggerRef.current = null;
  }, []);
  const applyPlacementAction = useCallback(
    (action: AlphaPlacementAction) => {
      const current = useAlphaGameStore.getState();
      const currentSnapshot = current.snapshot;
      const currentObservation = currentSnapshot?.observation;
      const latestAction = currentSnapshot?.humanLegalActions.find(
        (candidate): candidate is AlphaPlacementAction =>
          isAlphaPlacementAction(candidate) &&
          alphaPlacementActionKey(candidate) === alphaPlacementActionKey(action),
      );
      if (
        currentSnapshot === null ||
        latestAction === undefined ||
        currentObservation === undefined
      ) {
        setInteractionMessage("The game state changed. Drag the card again.");
        clearSelection(false);
        return;
      }
      const actionCountBefore = currentSnapshot.actionCount;
      const phaseBeforeAction = currentObservation.phase;
      const visibleCard = currentObservation.own.hand.find(
        (card) => card.cardInstanceId === alphaPlacementCardInstanceId(latestAction),
      );
      const cardName = visibleCard === undefined ? "card" : getAlphaDisplayName(visibleCard);
      applyHumanAction(latestAction);
      const nextSnapshot = useAlphaGameStore.getState().snapshot;
      if (nextSnapshot?.actionCount === actionCountBefore + 1) {
        completeTutorialConcepts(
          getTutorialConceptsForAcceptedAction(latestAction, phaseBeforeAction),
        );
        setInteractionMessage(
          `${placementActionTitle(latestAction, cardName)} in the ${titleCase(latestAction.laneId)} lane.`,
        );
        setPlacementPulseLaneId(latestAction.laneId);
        window.setTimeout(() => setPlacementPulseLaneId(undefined), reducedMotion ? 60 : 220);
      }
      clearSelection(false);
    },
    [applyHumanAction, clearSelection, completeTutorialConcepts, reducedMotion],
  );
  const drag = useAlphaCardDrag({
    getLaneAtPoint,
    onDrop: applyPlacementAction,
    onCancel: (reason) => {
      if (reason === "invalid")
        setInteractionMessage("That lane is not a legal target. The card returned to your hand.");
    },
  });
  useEffect(() => {
    if (!selectionActive) return;
    selectionHistorySequenceRef.current += 1;
    const marker = `alpha-card-selection-${String(selectionHistorySequenceRef.current)}`;
    const previousState =
      typeof window.history.state === "object" && window.history.state !== null
        ? (window.history.state as Record<string, unknown>)
        : {};
    window.history.pushState({ ...previousState, __fateAlphaSelection: marker }, "");
    let popped = false;
    const cancelSelectedCard = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      clearSelection(false);
      setInteractionMessage("Card selection cancelled.");
    };
    const cancelSelectedCardFromHistory = () => {
      popped = true;
      clearSelection(false);
      setInteractionMessage("Card selection cancelled.");
    };
    window.addEventListener("keydown", cancelSelectedCard);
    window.addEventListener("popstate", cancelSelectedCardFromHistory);
    return () => {
      window.removeEventListener("keydown", cancelSelectedCard);
      window.removeEventListener("popstate", cancelSelectedCardFromHistory);
      const currentState = window.history.state as { __fateAlphaSelection?: unknown } | null;
      if (!popped && currentState?.__fateAlphaSelection === marker) window.history.back();
    };
  }, [clearSelection, selectionActive]);
  const applyAttackAction = useCallback(
    (action: AlphaAttackAction) => {
      const current = useAlphaGameStore.getState();
      const currentSnapshot = current.snapshot;
      const currentObservation = currentSnapshot?.observation;
      const latestAction = currentSnapshot?.humanLegalActions.find(
        (candidate): candidate is AlphaAttackAction =>
          candidate.type === "attack" && attackActionKey(candidate) === attackActionKey(action),
      );
      if (
        currentSnapshot === null ||
        latestAction === undefined ||
        currentObservation === undefined
      ) {
        setInteractionMessage("The battle state changed. Choose a formation again.");
        clearSelection(false);
        return;
      }
      const actionCountBefore = currentSnapshot.actionCount;
      const phaseBeforeAction = currentObservation.phase;
      const preview = publicAttackPreview(currentObservation, latestAction);
      const targetControlBefore =
        currentObservation.lanes[preview.targetLaneId].opponent.land?.currentControl ?? 0;
      applyHumanAction(latestAction);
      const nextSnapshot = useAlphaGameStore.getState().snapshot;
      if (nextSnapshot?.actionCount === actionCountBefore + 1) {
        completeTutorialConcepts(
          getTutorialConceptsForAcceptedAction(latestAction, phaseBeforeAction),
        );
        const targetLaneId = latestAction.targetLaneId ?? latestAction.laneId;
        const kind = targetLaneId === latestAction.laneId ? "Direct" : "Flank";
        const targetControlAfter =
          nextSnapshot.observation.lanes[targetLaneId].opponent.land?.currentControl ?? 0;
        setCombat({
          ...preview,
          damage: Math.max(0, targetControlBefore - targetControlAfter),
          targetControlBefore,
          targetControlAfter,
          laneDefeated:
            currentObservation.lanes[targetLaneId].result === "active" &&
            nextSnapshot.observation.lanes[targetLaneId].result !== "active",
        });
        if (combatTimerRef.current !== null) window.clearTimeout(combatTimerRef.current);
        combatTimerRef.current = window.setTimeout(
          () => {
            setCombat(null);
            combatTimerRef.current = null;
          },
          reducedMotion ? 80 : 780,
        );
        setInteractionMessage(
          `${kind} Attack: ${titleCase(latestAction.laneId)} formation attacked ${titleCase(targetLaneId)} Land.`,
        );
      }
      clearSelection(false);
    },
    [applyHumanAction, clearSelection, completeTutorialConcepts, reducedMotion],
  );
  const formationDrag = useAlphaFormationDrag({
    getLaneAtPoint,
    onDrop: applyAttackAction,
    onCancel: (message) => setInteractionMessage(message),
  });
  const applyFieldDisbandAction = useCallback(
    (action: AlphaDisbandAction) => {
      const currentSnapshot = useAlphaGameStore.getState().snapshot;
      const currentObservation = currentSnapshot?.observation;
      const latestAction = currentSnapshot?.humanLegalActions.find(
        (candidate): candidate is AlphaDisbandAction =>
          (candidate.type === "disband-army" || candidate.type === "disband-commander") &&
          disbandActionKey(candidate) === disbandActionKey(action),
      );
      if (
        currentSnapshot === null ||
        currentObservation === undefined ||
        latestAction === undefined
      ) {
        setInteractionMessage("The game state changed. Drag the field card again.");
        return;
      }
      const cardInstanceId = disbandCardInstanceId(latestAction);
      const side = currentObservation.lanes[latestAction.laneId].own;
      const card =
        latestAction.type === "disband-army"
          ? side.armies.find((army) => army.cardInstanceId === cardInstanceId)
          : side.commander;
      const cardName =
        card === null || card === undefined ? "Field card" : getAlphaDisplayName(card);
      const actionCountBefore = currentSnapshot.actionCount;
      applyHumanAction(latestAction);
      const nextSnapshot = useAlphaGameStore.getState().snapshot;
      if (nextSnapshot?.actionCount === actionCountBefore + 1) {
        completeTutorialConcepts(
          getTutorialConceptsForAcceptedAction(latestAction, currentObservation.phase),
        );
        setInteractionMessage(
          `${cardName} was disbanded to your discard pile with no resource refund.`,
        );
      }
      clearSelection(false);
    },
    [applyHumanAction, clearSelection, completeTutorialConcepts],
  );
  const fieldDisbandDrag = useAlphaFieldDisbandDrag({
    isFriendlyDiscardAtPoint,
    onDrop: applyFieldDisbandAction,
    onCancel: (message) => setInteractionMessage(message),
  });
  const { cancelDrag } = drag;
  const legalTargetIds = formationDrag.state.active
    ? formationDrag.state.legalTargetLaneIds
    : drag.state.status === "dragging" || drag.state.status === "dropping"
      ? drag.state.legalLaneIds
      : selectedAttackSourceLaneId === undefined
        ? selectedLegalTargetIds
        : selectedAttackTargetIds;
  const objectiveRecommendation = useMemo(
    () =>
      observation === undefined
        ? null
        : getAlphaObjectiveRecommendation({
            observation,
            legalActions: snapshot?.humanLegalActions ?? [],
          }),
    [observation, snapshot?.humanLegalActions],
  );
  const tutorialHighlight =
    objectiveRecommendation === null
      ? null
      : getTutorialHighlight(
          {
            enabled: tutorialEnabled,
            completedConcepts: completedTutorialConcepts,
            dismissedCurrentHint: dismissedTutorialHint,
          },
          objectiveRecommendation,
        );
  const welcomeOpen = tutorialEnabled && welcomeStep !== null;
  const tutorialModalOpen = welcomeOpen || guideOpen;
  useEffect(() => {
    if (openingControllerRef.current === controller) return;
    openingControllerRef.current = controller;
    setOpeningDrawVisible(snapshot?.actionCount === 0);
  }, [controller, snapshot?.actionCount]);
  useEffect(() => {
    if (!openingDrawVisible || tutorialModalOpen) return;
    const timer = window.setTimeout(() => setOpeningDrawVisible(false), reducedMotion ? 160 : 760);
    return () => window.clearTimeout(timer);
  }, [openingDrawVisible, reducedMotion, tutorialModalOpen]);
  const terminalDialogOpen =
    snapshot !== null && (snapshot.failure !== null || observation?.status.type === "finished");
  const interactionBlocked =
    pending !== undefined ||
    inspectedCards !== undefined ||
    menuOpen ||
    combat !== null ||
    noTerritoryAnnouncement ||
    activePresentation !== undefined ||
    openingDrawVisible ||
    fieldDisbandDrag.state.active ||
    tutorialModalOpen ||
    terminalDialogOpen;
  const hoveredLegalLaneId =
    drag.state.hoveredLaneId !== null && drag.state.actionByLane.has(drag.state.hoveredLaneId)
      ? drag.state.hoveredLaneId
      : formationDrag.state.hoveredLaneId !== null &&
          formationDrag.state.legalTargetLaneIds.includes(formationDrag.state.hoveredLaneId)
        ? formationDrag.state.hoveredLaneId
        : undefined;

  const choosePending = useCallback((next: AlphaPendingAction, fromMenu = false) => {
    pendingFromMenuRef.current = fromMenu;
    pendingTriggerRef.current = document.activeElement as HTMLElement | null;
    setPending(next);
  }, []);

  useEffect(() => {
    const previous = selectionEpochRef.current;
    selectionEpochRef.current = { sessionToken, paused, contextStatus };
    const invalidated =
      previous.sessionToken !== sessionToken ||
      previous.paused !== paused ||
      (previous.contextStatus !== contextStatus && contextStatus === "lost");
    if (!invalidated) {
      return;
    }
    const timer = window.setTimeout(() => clearSelection(false), 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [clearSelection, contextStatus, paused, sessionToken]);

  useEffect(() => {
    if (
      !isHumanTurn ||
      contextStatus === "lost" ||
      interactionBlocked ||
      observation?.status.type !== "active"
    ) {
      cancelDrag("cancelled");
      formationDrag.cancel("Attack cancelled.");
    }
  }, [
    cancelDrag,
    contextStatus,
    formationDrag,
    interactionBlocked,
    isHumanTurn,
    observation?.status.type,
  ]);

  useEffect(() => {
    if (terminalDialogOpen) resultDialogRef.current?.focus();
  }, [terminalDialogOpen]);

  useEffect(() => {
    if (activePresentation === undefined) return;
    const duration = alphaPresentationDuration(activePresentation, {
      quick: quickAi,
      reducedMotion,
    });
    const timer = window.setTimeout(
      () => acknowledgePresentation(activePresentation.actionSequence),
      duration,
    );
    return () => window.clearTimeout(timer);
  }, [acknowledgePresentation, activePresentation, quickAi, reducedMotion]);

  useEffect(() => {
    if (
      !isAiTurn ||
      paused ||
      contextStatus === "lost" ||
      snapshot?.failure !== null ||
      tutorialModalOpen ||
      activePresentation !== undefined
    ) {
      return;
    }
    const delay = quickAi ? 0 : reducedMotion ? 30 : 350;
    const timer = window.setTimeout(() => stepAi(sessionToken), delay);
    return () => window.clearTimeout(timer);
  }, [
    contextStatus,
    isAiTurn,
    paused,
    quickAi,
    reducedMotion,
    sessionToken,
    snapshot?.failure,
    stepAi,
    tutorialModalOpen,
    activePresentation,
  ]);

  const autoProgression =
    observation === undefined
      ? null
      : getAlphaAutoProgression({
          phase: observation.phase,
          legalActions: snapshot?.humanLegalActions ?? [],
          canProgress:
            isHumanTurn === true &&
            contextStatus === "ready" &&
            snapshot?.failure === null &&
            !interactionBlocked &&
            !drag.isActive &&
            !formationDrag.state.active &&
            selectedCardId === undefined &&
            selectedAttackSourceLaneId === undefined &&
            !(
              observation.noTerritoryWarning?.threatenedPlayerId ===
                observation.perspectivePlayerId && observation.phase === "land"
            ),
        });

  useEffect(() => {
    if (autoProgression === null || snapshot === null) {
      progressionKeyRef.current = null;
      return;
    }
    const progressionKey = `${sessionToken}:${snapshot.actionCount}:${observation?.phase ?? "none"}`;
    if (progressionKeyRef.current === progressionKey) return;
    progressionKeyRef.current = progressionKey;
    const timer = window.setTimeout(
      () => {
        const current = useAlphaGameStore.getState();
        const currentSnapshot = current.snapshot;
        if (current.sessionToken !== sessionToken || currentSnapshot === null) return;
        const next = getAlphaAutoProgression({
          phase: currentSnapshot.observation.phase,
          legalActions: currentSnapshot.humanLegalActions,
          canProgress:
            currentSnapshot.observation.activePlayerId ===
              currentSnapshot.observation.perspectivePlayerId &&
            currentSnapshot.failure === null &&
            current.contextStatus === "ready" &&
            !(
              currentSnapshot.observation.noTerritoryWarning?.threatenedPlayerId ===
                currentSnapshot.observation.perspectivePlayerId &&
              currentSnapshot.observation.phase === "land"
            ),
        });
        if (next === null) return;
        const actionCountBefore = currentSnapshot.actionCount;
        setInteractionMessage(next.message);
        applyHumanAction(next.action);
        if (useAlphaGameStore.getState().snapshot?.actionCount === actionCountBefore + 1) {
          completeTutorialConcepts(
            getTutorialConceptsForAcceptedAction(next.action, currentSnapshot.observation.phase),
          );
        }
      },
      reducedMotion ? 0 : autoProgression.delayMs,
    );
    return () => {
      window.clearTimeout(timer);
      if (progressionKeyRef.current === progressionKey) progressionKeyRef.current = null;
    };
  }, [
    applyHumanAction,
    autoProgression,
    completeTutorialConcepts,
    observation?.phase,
    reducedMotion,
    sessionToken,
    formationDrag.state.active,
    selectedAttackSourceLaneId,
    snapshot,
  ]);

  useEffect(() => {
    if (observation?.status.type === "finished") completeTutorialConcepts(["victory"]);
  }, [completeTutorialConcepts, observation?.status.type]);

  useEffect(() => {
    const warning = observation?.noTerritoryWarning;
    const key =
      warning === undefined || warning === null
        ? null
        : `${warning.threatenedPlayerId}:${String(warning.deadlineTurnNumber)}`;
    if (key === null) {
      warningKeyRef.current = null;
      if (warningTimerRef.current !== null) window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = window.setTimeout(() => {
        setNoTerritoryAnnouncement(false);
        warningTimerRef.current = null;
      }, 0);
      return;
    }
    if (warningKeyRef.current === key) return;
    warningKeyRef.current = key;
    setNoTerritoryAnnouncement(true);
    if (warningTimerRef.current !== null) window.clearTimeout(warningTimerRef.current);
    warningTimerRef.current = window.setTimeout(
      () => {
        setNoTerritoryAnnouncement(false);
        warningTimerRef.current = null;
      },
      reducedMotion ? 120 : 1_450,
    );
  }, [observation?.noTerritoryWarning, reducedMotion]);

  useEffect(() => {
    if (observation === undefined) return;
    const previous = previousPublicObservationRef.current;
    previousPublicObservationRef.current = observation;
    if (previous === null || snapshot === null || snapshot.actionCount === 0) return;
    const changedLaneId = (["left", "center", "right"] as const).find(
      (laneId) =>
        publicLaneSignature(previous, laneId) !== publicLaneSignature(observation, laneId),
    );
    if (changedLaneId === undefined) return;
    setAiFocusLaneId(changedLaneId);
    if (aiFocusTimerRef.current !== null) window.clearTimeout(aiFocusTimerRef.current);
    aiFocusTimerRef.current = window.setTimeout(
      () => {
        setAiFocusLaneId(undefined);
        aiFocusTimerRef.current = null;
      },
      reducedMotion ? 80 : 620,
    );
  }, [observation, reducedMotion, snapshot]);

  useEffect(
    () => () => {
      if (combatTimerRef.current !== null) window.clearTimeout(combatTimerRef.current);
      if (warningTimerRef.current !== null) window.clearTimeout(warningTimerRef.current);
      if (aiFocusTimerRef.current !== null) window.clearTimeout(aiFocusTimerRef.current);
    },
    [],
  );

  const activeDragClientY = formationDrag.state.active
    ? formationDrag.state.currentY
    : fieldDisbandDrag.state.active
      ? fieldDisbandDrag.state.currentY
      : drag.state.status === "dragging" || drag.state.status === "dropping"
        ? drag.state.currentY
        : undefined;
  const battlefieldCamera = useBattlefieldCamera({
    blocked:
      interactionBlocked ||
      drag.isActive ||
      formationDrag.state.active ||
      fieldDisbandDrag.state.active ||
      contextStatus === "lost",
    result: terminalDialogOpen,
    inspecting: inspectedCards !== undefined || menuOpen || tutorialModalOpen,
    combat: combat !== null,
    attack: selectedAttackSourceLaneId !== undefined || formationDrag.state.active,
    placement: selectedCardId !== undefined || drag.isActive,
    aiTurn: isAiTurn === true,
    selectedCard: selectedCardId !== undefined,
    focusLaneId: combat?.targetLaneId ?? aiFocusLaneId ?? hoveredLegalLaneId,
    dragClientY: activeDragClientY,
    reducedMotion,
  });

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (shell === null) return;
    shell.scrollLeft = 0;
    shell.scrollTop = 0;
  }, [
    activePresentation?.actionSequence,
    observation?.phase,
    observation?.turnNumber,
    pending,
    selectedAttackSourceLaneId,
    selectedCardId,
  ]);

  if (controller === null || snapshot === null || observation === undefined) return null;

  const chooseLane = (laneId: LaneViewId) => {
    if (drag.isActive || formationDrag.state.active || fieldDisbandDrag.state.active) return;
    if (selectedAttackSourceLaneId !== undefined) {
      const targetAction = attackActionsBySource.get(selectedAttackSourceLaneId)?.get(laneId);
      if (targetAction === undefined) {
        setInteractionMessage("That Land is not a legal attack target for this formation.");
        return;
      }
      applyAttackAction(targetAction);
      return;
    }
    if (selectedCard === undefined) {
      setInteractionMessage("Choose a playable card first, then drag it or tap a glowing lane.");
      return;
    }
    const targetAction = selectedCardActions.find(
      (action): action is AlphaPlacementAction =>
        isAlphaPlacementAction(action) && action.laneId === laneId,
    );
    if (targetAction === undefined) {
      setInteractionMessage("That lane is not a legal target for this card.");
      return;
    }
    applyPlacementAction(targetAction);
  };
  const confirmPending = () => {
    if (pending === undefined) return;
    const actionCountBefore = snapshot.actionCount;
    const phaseBeforeAction = observation.phase;
    applyHumanAction(pending.action);
    if (useAlphaGameStore.getState().snapshot?.actionCount === actionCountBefore + 1) {
      completeTutorialConcepts(
        getTutorialConceptsForAcceptedAction(pending.action, phaseBeforeAction),
      );
    }
    pendingFromMenuRef.current = false;
    clearSelection(false);
  };
  const cancelPending = () => {
    const reopenMenu = pendingFromMenuRef.current;
    pendingFromMenuRef.current = false;
    clearSelection(!reopenMenu);
    if (reopenMenu) setMenuOpen(true);
  };
  const exportPayload = (filename: string, label: string, payload: unknown) => {
    const json = JSON.stringify(payload, null, 2);
    if (downloadJson(filename, json)) {
      setExportFallback(null);
      setExportMessage(`${label} downloaded.`);
    } else {
      setExportFallback({ label, json });
      setExportMessage(`${label} download was blocked. Copy the JSON below instead.`);
    }
  };
  const exportReplay = () => {
    const verification = controller.verifyReplay();
    if (!verification.ok) {
      setExportMessage(`Replay verification failed: ${verification.message}`);
      return;
    }
    exportPayload(
      alphaExportFilename("replay", snapshot.config.seed, snapshot.actionCount),
      `Verified Replay V${String(controller.exportReplay().replayVersion)}`,
      controller.exportReplay(),
    );
  };
  const exportFailure = () =>
    exportPayload(
      alphaExportFilename("failure", snapshot.config.seed, snapshot.actionCount),
      "Failure report",
      controller.exportFailureReport(),
    );
  const copyFallback = async () => {
    if (exportFallback === null) return;
    try {
      await navigator.clipboard.writeText(exportFallback.json);
      setExportMessage(`${exportFallback.label} JSON copied.`);
    } catch {
      setExportMessage("Copy was blocked. Select the JSON text and copy it manually.");
    }
  };
  const humanWon =
    observation.status.type === "finished" &&
    observation.status.winnerPlayerId === observation.perspectivePlayerId;
  const noTerritoryResult =
    observation.status.type === "finished" && observation.status.reason === "no-territory";
  const targetMode =
    selectedAttackSourceLaneId !== undefined || formationDrag.state.active ? "opponent" : "own";
  const selectedAttackTargetId =
    selectedAttackSourceLaneId === undefined
      ? undefined
      : (hoveredLegalLaneId ??
        (selectedAttackTargetIds.length === 1 ? selectedAttackTargetIds[0] : undefined));
  const selectedAttackAction =
    selectedAttackSourceLaneId === undefined || selectedAttackTargetId === undefined
      ? undefined
      : attackActionsBySource.get(selectedAttackSourceLaneId)?.get(selectedAttackTargetId);
  const attackPreview =
    selectedAttackAction === undefined
      ? undefined
      : publicAttackPreview(observation, selectedAttackAction);
  const dragLabelByLane = new Map<LaneId, string>();
  if (drag.state.status === "dragging" || drag.state.status === "dropping") {
    for (const [laneId, action] of drag.state.actionByLane) {
      dragLabelByLane.set(
        laneId,
        drag.state.hoveredLaneId === laneId
          ? `Release to ${alphaPlacementLabel(action).toLowerCase()}`
          : alphaPlacementLabel(action),
      );
    }
  } else {
    for (const action of selectedCardActions) {
      if (isAlphaPlacementAction(action))
        dragLabelByLane.set(action.laneId, alphaPlacementLabel(action));
    }
  }
  const latestAiSummary = snapshot.aiSummaries.at(-1)?.summary;
  const statusMessage =
    interactionMessage ??
    validationMessage ??
    (isAiTurn
      ? paused
        ? "Opponent turn paused."
        : (latestAiSummary ?? "Opponent is considering one action…")
      : (objectiveRecommendation?.message ?? "Choose a card or formation."));
  const handCollapsed =
    selectedAttackSourceLaneId !== undefined ||
    formationDrag.state.active ||
    combat !== null ||
    inspectedCards !== undefined ||
    menuOpen ||
    tutorialModalOpen ||
    terminalDialogOpen;
  const openInspector = (cards: readonly VisibleCardViewV1[]) => {
    inspectorTriggerRef.current = document.activeElement as HTMLElement | null;
    setInspectedCards(cards);
  };
  const closeInspector = () => {
    setInspectedCards(undefined);
    const trigger = inspectorTriggerRef.current;
    inspectorTriggerRef.current = null;
    window.setTimeout(() => trigger?.focus(), 0);
  };
  const openMenu = () => {
    menuTriggerRef.current = document.activeElement as HTMLElement | null;
    setMenuOpen(true);
  };
  const closeMenu = () => {
    setMenuOpen(false);
    const trigger = menuTriggerRef.current;
    menuTriggerRef.current = null;
    window.setTimeout(() => trigger?.focus(), 0);
  };
  const phaseAction = directActions.find(
    (
      action,
    ): action is
      Extract<GameAction, { type: "advance-phase" }> | Extract<GameAction, { type: "end-turn" }> =>
      action.type === "advance-phase" || action.type === "end-turn",
  );
  const territoryDeadline =
    observation.phase === "land" &&
    observation.noTerritoryWarning?.threatenedPlayerId === observation.perspectivePlayerId;
  const phaseActionLabel =
    phaseAction === undefined
      ? null
      : territoryDeadline
        ? "End Phase · Lose"
        : phaseAction.type === "end-turn"
          ? "End Turn"
          : `End ${titleCase(observation.phase)} Phase`;
  const phaseActionEnabled =
    phaseAction !== undefined && isHumanTurn === true && !interactionBlocked;
  const activatePhaseAction = () => {
    if (phaseAction === undefined || !phaseActionEnabled) return;
    const choice: AlphaPendingAction = {
      action: phaseAction,
      title: territoryDeadline
        ? "End Land Phase and lose the match?"
        : phaseAction.type === "end-turn"
          ? "End Turn?"
          : `End ${titleCase(observation.phase)} Phase?`,
      description: territoryDeadline
        ? "You have no active Land while your opponent's Kingdom is Complete. Found a Land now, or confirm to end the match."
        : phaseAction.type === "end-turn"
          ? "Discard your remaining hand, clear temporary turn resources, and hand play to the opponent."
          : `Finish the current ${titleCase(observation.phase)} Phase.`,
    };
    if (phaseAction.type !== "end-turn") {
      choosePending(choice);
      return;
    }
    const actionCountBefore = snapshot.actionCount;
    applyHumanAction(phaseAction);
    if (useAlphaGameStore.getState().snapshot?.actionCount === actionCountBefore + 1) {
      completeTutorialConcepts(["cleanup"]);
      setInteractionMessage("Turn ended. Cleanup and handoff are resolving.");
    }
    clearSelection(false);
  };
  const selectedHandIndex =
    selectedCardId === undefined
      ? -1
      : observation.own.hand.findIndex((card) => card.cardInstanceId === selectedCardId);
  const phaseActionSide =
    selectedHandIndex >= Math.ceil(observation.own.hand.length / 2) ? "left" : "right";
  const hudLayout = createMatchHudLayout(viewport.width, viewport.height, phaseActionSide);
  const hudModel = createMatchHudModel({
    observation,
    humanDeckId: snapshot.config.humanDeckId,
    aiDeckId: snapshot.config.aiDeckId,
    isAiTurn: isAiTurn === true,
    paused,
    quickAi,
    statusMessage,
    phaseActionLabel,
    phaseActionEnabled,
    territoryDeadline,
  });
  const selectAttackSource = (laneId: LaneId) => {
    const attackTargets = attackActionsBySource.get(laneId);
    if (attackTargets === undefined) return;
    if (fieldDisbandDrag.consumeSuppressedClick()) return;
    if (formationDrag.consumeSuppressedClick(laneId)) return;
    setSelectedCardId(undefined);
    setSelectedAttackSourceLaneId(laneId);
    setPending(undefined);
    setInteractionMessage(
      attackTargets.size === 1
        ? "Direct Attack ready. Tap the glowing enemy Land or drag the formation."
        : "Flank Attack ready. Choose a glowing enemy Land or drag the formation.",
    );
  };

  return (
    <main
      ref={shellRef}
      className="alpha-shell"
      data-testid="alpha-player-shell"
      data-context-status={contextStatus}
      data-human-player={observation.perspectivePlayerId}
      data-action-count={snapshot.actionCount}
      data-turn-number={observation.turnNumber}
      data-phase={observation.phase}
      data-human-turn={isHumanTurn}
      data-match-status={observation.status.type}
      data-presentation-kind={activePresentation?.presentationKind}
      aria-busy={(isAiTurn && !paused) || activePresentation !== undefined || openingDrawVisible}
    >
      <div
        ref={battlefieldSurfaceRef}
        className="alpha-battlefield"
        aria-label="Pan battlefield"
        data-alpha-perspective-surface="physical-v1"
        {...battlefieldCamera.panSurfaceProps}
      >
        <Battlefield3D
          observation={observation}
          selectedLaneId={hoveredLegalLaneId}
          legalTargetIds={legalTargetIds}
          targetMode={targetMode}
          placementPulseLaneId={placementPulseLaneId}
          attackSourceLaneIds={[...attackActionsBySource.keys()]}
          selectedAttackSourceLaneId={selectedAttackSourceLaneId}
          combat={combat}
          cameraState={battlefieldCamera.cameraState}
          reducedMotion={reducedMotion}
          interactionBlocked={interactionBlocked}
          selectedHandCardId={selectedCardId}
          handLegalCardIds={handLegalCardIds}
          handDragState={drag.state}
          draggedHandCard={drag.draggedCard}
          handCollapsed={handCollapsed}
          isHumanTurn={isHumanTurn === true}
          focusedHandCardId={focusedHandCardId}
          hudLayout={hudLayout}
          hudModel={hudModel}
          onChooseLane={chooseLane}
          onSelectAttack={selectAttackSource}
          onInspectCards={openInspector}
          onContextStatusChange={setContextStatus}
          onHandScreenLayoutChange={setHandScreenLayout}
        />
      </div>
      <PortraitBattlefield
        observation={observation}
        camera={battlefieldCamera.cameraState}
        legalTargetIds={legalTargetIds}
        hoveredLaneId={hoveredLegalLaneId}
        targetMode={targetMode}
        dragging={
          drag.state.status === "dragging" ||
          drag.state.status === "dropping" ||
          formationDrag.state.active ||
          fieldDisbandDrag.state.active
        }
        placementPulseLaneId={placementPulseLaneId}
        attackActionsBySource={attackActionsBySource}
        disbandActionsByLane={disbandActionsByLane}
        selectedAttackSourceLaneId={selectedAttackSourceLaneId}
        attackPreview={attackPreview}
        combat={combat}
        interactionBlocked={interactionBlocked}
        laneRef={(laneId) => (element) => {
          if (element === null) laneRefs.current.delete(laneId);
          else laneRefs.current.set(laneId, element);
        }}
        dragLabelByLane={dragLabelByLane}
        onChooseLane={chooseLane}
        onSelectAttack={selectAttackSource}
        onFormationPointerDown={(laneId, actions, disbandActions) => (event) => {
          fieldDisbandDrag.beginPress(disbandActions, event);
          formationDrag.beginPress(laneId, actions, event);
        }}
        onInspectCards={openInspector}
      />
      <DiscardZones
        observation={observation}
        legalActions={snapshot.humanLegalActions}
        blocked={interactionBlocked || !isHumanTurn}
        dropActive={fieldDisbandDrag.state.active}
        dropHovered={fieldDisbandDrag.state.hovered}
        ownTargetRef={friendlyDiscardRef}
        onChoose={choosePending}
      />
      {formationDrag.state.active && formationDrag.state.sourceLaneId !== null ? (
        <div
          className="alpha-formation-drag-layer"
          aria-hidden="true"
          style={{
            transform: `translate3d(${String(formationDrag.state.currentX + 14)}px, ${String(
              formationDrag.state.currentY - 54,
            )}px, 0)`,
          }}
        >
          <b>{titleCase(formationDrag.state.sourceLaneId)} formation</b>
          <small>Release on a glowing enemy Land to attack</small>
        </div>
      ) : null}
      {fieldDisbandDrag.state.active ? (
        <div
          className="alpha-formation-drag-layer alpha-field-disband-drag-layer"
          aria-hidden="true"
          style={{
            transform: `translate3d(${String(fieldDisbandDrag.state.currentX + 14)}px, ${String(
              fieldDisbandDrag.state.currentY - 54,
            )}px, 0)`,
          }}
        >
          <b>Field card</b>
          <small>Release on your glowing discard pile to disband</small>
        </div>
      ) : null}

      <SemanticHudOverlay
        layout={hudLayout}
        model={hudModel}
        onOpenMenu={openMenu}
        onPauseAi={() => setPaused(!paused)}
        onFastAi={() => setQuickAi(true)}
        onPhaseAction={activatePhaseAction}
      />
      <ActionPresentationDirector
        transaction={activePresentation}
        openingObservation={
          openingDrawVisible && activePresentation === undefined ? observation : undefined
        }
        duration={
          activePresentation === undefined
            ? reducedMotion
              ? 160
              : 760
            : alphaPresentationDuration(activePresentation, {
                quick: quickAi,
                reducedMotion,
              })
        }
      />
      {activePresentation === undefined ? null : (
        <p
          className={`alpha-action-caption is-${activePresentation.presentationKind}`}
          role="status"
          aria-live="polite"
        >
          <strong>
            {activePresentation.actorPlayerId === observation.perspectivePlayerId
              ? "You"
              : "Opponent"}
          </strong>{" "}
          {publicActionSummary(activePresentation.action)}
        </p>
      )}
      {observation.noTerritoryWarning !== undefined && observation.noTerritoryWarning !== null ? (
        <NoTerritoryPresentation
          warning={observation.noTerritoryWarning}
          perspectivePlayerId={observation.perspectivePlayerId}
          announcement={noTerritoryAnnouncement}
        />
      ) : null}
      {contextStatus === "lost" ? (
        <p className="alpha-context-alert" role="alert">
          The 3D scene paused safely. Resume after graphics context restoration.
        </p>
      ) : null}

      <AlphaHand
        observation={observation}
        legalActions={snapshot.humanLegalActions}
        isHumanTurn={isHumanTurn === true}
        blocked={interactionBlocked}
        collapsed={handCollapsed}
        selectedCardId={selectedCardId}
        dragState={drag.state}
        tutorialHighlight={
          tutorialHighlight === "land" ||
          tutorialHighlight === "commander" ||
          tutorialHighlight === "army"
            ? tutorialHighlight
            : null
        }
        onSelect={(card, availability) => {
          if (drag.consumeSuppressedClick(card.cardInstanceId)) return;
          if (selectedCardId === card.cardInstanceId) {
            clearSelection(false);
            setInteractionMessage("Card selection cancelled.");
            return;
          }
          if (!availability.playable) {
            setInteractionMessage(availability.summary);
            return;
          }
          const eventAction = snapshot.humanLegalActions.find(
            (action) =>
              action.type === "play-event" && action.cardInstanceId === card.cardInstanceId,
          );
          if (eventAction !== undefined) {
            choosePending({
              action: eventAction,
              title: `Play ${getAlphaDisplayName(card)}?`,
              description:
                "Resolve this Event now, then send it to discard. The action window remains open.",
            });
            setInteractionMessage("Review this Event, then confirm its authoritative effect.");
            return;
          }
          setSelectedCardId(card.cardInstanceId);
          setSelectedAttackSourceLaneId(undefined);
          setPending(undefined);
          setInteractionMessage("Lift the card to a glowing slot, or tap a legal lane.");
        }}
        onInspect={(card) => openInspector([card])}
        onPointerDown={(card, actions, event) => drag.beginPress(card, actions, event)}
        screenLayout={handScreenLayout}
        onFocusCard={setFocusedHandCardId}
      />

      {isHumanTurn ? (
        <>
          <AlphaSupplyChoicePanel
            actions={directActions}
            disabled={interactionBlocked}
            observation={observation}
            onChoose={choosePending}
          />
        </>
      ) : null}

      {pending !== undefined ? (
        <AlphaActionConfirmation
          pending={pending}
          highlighted={tutorialHighlight === "confirm"}
          onConfirm={confirmPending}
          onCancel={cancelPending}
        />
      ) : null}
      {inspectedCards !== undefined ? (
        <AlphaCardInspector cards={inspectedCards} onClose={closeInspector} />
      ) : null}
      {welcomeOpen && welcomeStep !== null ? (
        <AlphaTutorialOverlay
          step={welcomeStep}
          onBack={previousWelcome}
          onNext={nextWelcome}
          onSkip={skipWelcome}
        />
      ) : null}
      {guideOpen ? (
        <AlphaQuickGuide onClose={() => setGuideOpen(false)} onRestartWelcome={restartWelcome} />
      ) : null}

      {menuOpen && snapshot.failure === null && observation.status.type === "active" ? (
        <AlphaGameplayMenu
          phase={observation.phase}
          aiSummaries={snapshot.aiSummaries}
          canExportReplay={!isAiTurn}
          isAiTurn={isAiTurn === true}
          paused={paused}
          quickAi={quickAi}
          reducedMotion={reducedMotion}
          onClose={closeMenu}
          onHowToPlay={() => {
            closeMenu();
            setGuideOpen(true);
          }}
          onExportReplay={() => {
            exportReplay();
            closeMenu();
          }}
          onPause={() => setPaused(!paused)}
          onFastAi={() => setQuickAi(true)}
          onResetTableView={() => {
            battlefieldCamera.resetOverview();
            closeMenu();
          }}
          onNewMatch={() => {
            closeMenu();
            leave();
          }}
          onDeveloperMode={() => {
            closeMenu();
            onOpenDeveloperMode();
          }}
        >
          {isHumanTurn ? (
            <AlphaCommandChoicePanel
              actions={directActions}
              disabled={pending !== undefined || inspectedCards !== undefined || drag.isActive}
              observation={observation}
              onChoose={(choice) => {
                closeMenu();
                choosePending(choice, true);
              }}
            />
          ) : null}
        </AlphaGameplayMenu>
      ) : null}
      {exportMessage === null ? null : (
        <span className="alpha-export-message" aria-live="polite">
          {exportMessage}
        </span>
      )}
      {snapshot.failure === null &&
      observation.status.type === "active" &&
      pending === undefined &&
      exportFallback !== null ? (
        <aside className="alpha-export-fallback alpha-export-fallback--floating" role="alert">
          <label>
            Copyable {exportFallback.label} JSON
            <textarea readOnly value={exportFallback.json} />
          </label>
          <button className="alpha-button" type="button" onClick={copyFallback}>
            Copy JSON
          </button>
        </aside>
      ) : null}

      {snapshot.failure !== null ? (
        <aside
          ref={resultDialogRef}
          className="alpha-result"
          role="dialog"
          aria-modal="true"
          aria-labelledby="alpha-failure-title"
          tabIndex={-1}
        >
          <p className="alpha-kicker">Public Beta diagnostic stop</p>
          <h2 id="alpha-failure-title">Match paused safely</h2>
          <p>Your deterministic match stopped before another action could be applied.</p>
          <p>{snapshot.failure.message}</p>
          <dl className="alpha-result__facts">
            <div>
              <dt>Seed</dt>
              <dd>{snapshot.config.seed}</dd>
            </div>
            <div>
              <dt>Turn / phase</dt>
              <dd>
                {observation.turnNumber} / {observation.phase}
              </dd>
            </div>
            <div>
              <dt>Actions</dt>
              <dd>{snapshot.actionCount}</dd>
            </div>
            <div>
              <dt>Failure</dt>
              <dd>{snapshot.failure.code}</dd>
            </div>
          </dl>
          <button className="alpha-button" type="button" onClick={exportFailure}>
            Export failure report
          </button>
          <button className="alpha-button" type="button" onClick={exportReplay}>
            Export verified replay
          </button>
          <button
            className="alpha-button alpha-button--primary"
            type="button"
            onClick={() => {
              resetTutorial(tutorialMode);
              start(snapshot.config);
            }}
          >
            Rematch
          </button>
          <button className="alpha-button alpha-button--secondary" type="button" onClick={leave}>
            New match
          </button>
          <button
            className="alpha-button alpha-button--secondary"
            type="button"
            onClick={onOpenDeveloperMode}
          >
            Developer Mode
          </button>
          {exportMessage === null ? null : <p aria-live="polite">{exportMessage}</p>}
          {exportFallback === null ? null : (
            <div className="alpha-export-fallback">
              <label>
                Copyable {exportFallback.label} JSON
                <textarea readOnly value={exportFallback.json} />
              </label>
              <button className="alpha-button" type="button" onClick={copyFallback}>
                Copy JSON
              </button>
            </div>
          )}
        </aside>
      ) : observation.status.type === "finished" ? (
        <aside
          ref={resultDialogRef}
          className="alpha-result"
          role="dialog"
          aria-modal="true"
          aria-labelledby="alpha-result-title"
          tabIndex={-1}
        >
          <p className="alpha-kicker">Public Beta result</p>
          <h2 id="alpha-result-title">
            {noTerritoryResult
              ? humanWon
                ? "Total Domination"
                : "No Territory"
              : humanWon
                ? "Victory"
                : "Defeat"}
          </h2>
          <p>
            {noTerritoryResult
              ? humanWon
                ? "The opposing kingdom failed to restore an active Land."
                : "Your kingdom ended its deadline Land Phase without active territory."
              : "Two lanes were claimed."}
          </p>
          <dl className="alpha-result__facts">
            <div>
              <dt>Winner</dt>
              <dd>{humanWon ? "You" : "AI"}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{noTerritoryResult ? "No Territory" : "Two victory banners"}</dd>
            </div>
            <div>
              <dt>Final turn</dt>
              <dd>{observation.turnNumber}</dd>
            </div>
            <div>
              <dt>Actions</dt>
              <dd>{snapshot.actionCount}</dd>
            </div>
            <div>
              <dt>Banners</dt>
              <dd>
                You {observation.own.victoryBanners} / AI {observation.opponent.victoryBanners}
              </dd>
            </div>
            <div>
              <dt>Seed</dt>
              <dd>{snapshot.config.seed}</dd>
            </div>
          </dl>
          <button className="alpha-button" type="button" onClick={exportReplay}>
            Export verified replay
          </button>
          <button
            className="alpha-button alpha-button--primary"
            type="button"
            onClick={() => {
              resetTutorial(tutorialMode);
              start(snapshot.config);
            }}
          >
            Rematch
          </button>
          <button className="alpha-button alpha-button--secondary" type="button" onClick={leave}>
            New match
          </button>
          <button
            className="alpha-button alpha-button--secondary"
            type="button"
            onClick={onOpenDeveloperMode}
          >
            Developer Mode
          </button>
          {exportMessage === null ? null : <p aria-live="polite">{exportMessage}</p>}
          {exportFallback === null ? null : (
            <div className="alpha-export-fallback">
              <label>
                Copyable {exportFallback.label} JSON
                <textarea readOnly value={exportFallback.json} />
              </label>
              <button className="alpha-button" type="button" onClick={copyFallback}>
                Copy JSON
              </button>
            </div>
          )}
        </aside>
      ) : null}
    </main>
  );
}

export function AlphaApp(props: AlphaAppProps) {
  const snapshot = useAlphaGameStore((state) => state.snapshot);
  return snapshot === null ? <SetupScreen {...props} /> : <AlphaBattleScreen {...props} />;
}
